import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../../common/BadgeMultiSelect/BadgeMultiSelect.scss";
import "./MilestoneRecipientRoles.scss";
import "./MilestoneSingleOptionPicker.scss";
import {
  computeAnchoredPopoverPosition,
  getAnchoredPopoverStyle,
  useAnchoredPopoverPosition,
} from "./milestoneAnchoredPopover";

const INITIAL_POPOVER_POSITION = {
  top: 12,
  bottom: null,
  left: 12,
  openUpward: false,
  listMaxHeight: 220,
  width: 248,
};

const OptionPickerPopover = ({
  open,
  onClose,
  position,
  title,
  subtitle,
  options,
  selectedValue,
  onSelect,
  popoverRef,
  showSearch = true,
  searchPlaceholder = "Search...",
  emptyMessage = "No options found",
}) => {
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((opt) =>
      String(opt?.label || opt?.value || "")
        .toLowerCase()
        .includes(term),
    );
  }, [options, search]);

  if (!open) return null;

  const popover = (
    <div
      className="badge-multi-select__popover badge-multi-select__popover--anchored milestone-recipient-roles__popover"
      ref={popoverRef}
      style={getAnchoredPopoverStyle(position)}
    >
      <div className="badge-multi-select__popover-head">
        <div>
          <strong>{title}</strong>
          <span>{subtitle}</span>
        </div>
        <button type="button" onClick={onClose} aria-label={`Close ${title}`}>
          ×
        </button>
      </div>

      {showSearch && (
        <div className="milestone-recipient-roles__search-wrap">
          <input
            type="text"
            className="form-control form-control-sm milestone-recipient-roles__search"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
      )}

      <div
        className="badge-multi-select__popover-list"
        style={{ maxHeight: `${position.listMaxHeight}px` }}
      >
        {filteredOptions.length ? (
          filteredOptions.map((opt) => {
            const selected = selectedValue === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={selected ? "is-selected" : ""}
                onClick={() => onSelect(opt.value)}
              >
                <span>{opt.label}</span>
                {selected ? <small>Selected</small> : null}
              </button>
            );
          })
        ) : (
          <div className="milestone-recipient-roles__empty">{emptyMessage}</div>
        )}
      </div>
    </div>
  );

  return createPortal(popover, document.body);
};

const MilestoneSingleOptionPicker = ({
  value = "",
  options = [],
  disabled = false,
  onChange,
  showSearch = true,
  addLabel = "+ Select",
  changeLabel = "+ Change",
  popoverTitle = "Select option",
  popoverSubtitle = "Choose one",
  searchPlaceholder = "Search...",
  emptyMessage = "No options found",
  placeholder = "—",
  layout = "chip",
  emptyPlaceholder = "Select…",
  disabledPlaceholder = "Not available",
  allowClear = true,
  invalid = false,
}) => {
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(INITIAL_POPOVER_POSITION);

  useAnchoredPopoverPosition(open, triggerRef, setPosition, { showSearch });

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value) || null,
    [options, value],
  );

  useEffect(() => {
    if (!open) return undefined;

    const handleMouseDown = (event) => {
      const inPopover = popoverRef.current?.contains(event.target);
      const inTrigger = triggerRef.current?.contains(event.target);
      if (inPopover || inTrigger) return;
      setOpen(false);
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open]);

  const openPicker = (event) => {
    if (disabled || options.length === 0) return;
    setPosition(computeAnchoredPopoverPosition(event.currentTarget, { showSearch }));
    setOpen((prev) => !prev);
  };

  const selectValue = (nextValue) => {
    if (disabled || !onChange) return;
    onChange(nextValue);
    setOpen(false);
  };

  const clearValue = () => {
    if (disabled || !onChange) return;
    onChange("");
  };

  if (disabled && !selectedOption && layout !== "inline-field") {
    return <span className="text-muted small">{placeholder}</span>;
  }

  const isInlineField = layout === "inline-field";
  const fieldLabel = selectedOption
    ? selectedOption.label
    : disabled
      ? disabledPlaceholder
      : emptyPlaceholder;

  if (isInlineField) {
    const canOpenPicker = !disabled && options.length > 0;
    const openEmptyField = (event) => {
      if (!canOpenPicker || selectedOption) return;
      openPicker(event);
    };

    return (
      <div className="milestone-single-option-picker milestone-single-option-picker--inline-field">
        <div
          ref={!selectedOption && canOpenPicker ? triggerRef : null}
          className={[
            "milestone-single-option-picker__field",
            !selectedOption ? "is-empty" : "",
            !selectedOption && canOpenPicker ? "is-clickable" : "",
            disabled ? "is-disabled" : "",
            invalid ? "is-invalid" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={openEmptyField}
          onKeyDown={
            !selectedOption && canOpenPicker
              ? (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openPicker(event);
                  }
                }
              : undefined
          }
          role={!selectedOption && canOpenPicker ? "button" : undefined}
          tabIndex={!selectedOption && canOpenPicker ? 0 : undefined}
        >
          <span
            className="milestone-single-option-picker__field-label"
            title={fieldLabel}
          >
            {fieldLabel}
          </span>
          {canOpenPicker && selectedOption && (
            <div className="milestone-single-option-picker__field-actions">
              {allowClear && (
                <button
                  type="button"
                  className="milestone-single-option-picker__field-clear"
                  aria-label={`Clear ${selectedOption.label}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    clearValue();
                  }}
                >
                  ×
                </button>
              )}
              <button
                ref={triggerRef}
                type="button"
                className="milestone-single-option-picker__field-edit"
                aria-label={changeLabel}
                title={changeLabel}
                onClick={(event) => {
                  event.stopPropagation();
                  openPicker(event);
                }}
              >
                <i className="fas fa-pencil-alt" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>

        {!disabled && options.length === 0 && !selectedOption && emptyMessage ? (
          <span className="text-muted small">{emptyMessage}</span>
        ) : null}

        <OptionPickerPopover
          open={open}
          onClose={() => setOpen(false)}
          position={position}
          title={popoverTitle}
          subtitle={popoverSubtitle}
          options={options}
          selectedValue={value}
          onSelect={selectValue}
          popoverRef={popoverRef}
          showSearch={showSearch}
          searchPlaceholder={searchPlaceholder}
          emptyMessage={emptyMessage}
        />
      </div>
    );
  }

  return (
    <div className="milestone-single-option-picker">
      {selectedOption ? (
        <span className="milestone-single-option-picker__chip">
          <span className="milestone-single-option-picker__chip-label" title={selectedOption.label}>
            {selectedOption.label}
          </span>
          {!disabled && (
            <button
              type="button"
              className="milestone-single-option-picker__chip-remove"
              aria-label={`Remove ${selectedOption.label}`}
              onClick={clearValue}
            >
              ×
            </button>
          )}
        </span>
      ) : null}

      {!disabled && options.length > 0 && (
        <button
          ref={triggerRef}
          type="button"
          className="badge-multi-select__add milestone-recipient-roles__add"
          onClick={openPicker}
        >
          {selectedOption ? changeLabel : addLabel}
        </button>
      )}

      {!disabled && options.length === 0 && !selectedOption && (
        <span className="text-muted small">{emptyMessage}</span>
      )}

      <OptionPickerPopover
        open={open}
        onClose={() => setOpen(false)}
        position={position}
        title={popoverTitle}
        subtitle={popoverSubtitle}
        options={options}
        selectedValue={value}
        onSelect={selectValue}
        popoverRef={popoverRef}
        showSearch={showSearch}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
      />
    </div>
  );
};

export default MilestoneSingleOptionPicker;
